import { getPublishValidationIssues } from './productValidation';
import type { CustomField, ProductDeliveryType, ProductFormData, StoreProductType } from '../types/productCreation';

export function isProductPublished(status?: string) {
  return status === 'active';
}

export function getProductStatusLabel(status?: string) {
  return isProductPublished(status) ? 'Published' : 'Unpublished';
}

export function getProductPublicUrl(productId: string) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/product-checkout/${productId}`;
}

export interface ProductCheckoutNavigationState {
  values?: Record<string, string>;
  designTokens?: {
    fontFamily?: string;
    primaryFontFamily?: string;
    secondaryFontFamily?: string;
    primary_font_family?: string;
    secondary_font_family?: string;
    primaryColor?: string;
    secondaryColor?: string;
    tertiaryColor?: string;
    forthColor?: string;
    templateName?: string;
  };
}

function getProductCheckoutSessionKey(productId: string) {
  return `product-checkout:${productId}`;
}

export function stashProductCheckoutState(
  productId: string,
  state: ProductCheckoutNavigationState
) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      getProductCheckoutSessionKey(productId),
      JSON.stringify(state)
    );
  } catch {
    // Ignore quota / private-mode errors; checkout still opens without prefilled state.
  }
}

export function readProductCheckoutState(
  productId: string
): ProductCheckoutNavigationState | null {
  if (typeof window === 'undefined' || !productId) return null;
  try {
    const raw = sessionStorage.getItem(getProductCheckoutSessionKey(productId));
    if (!raw) return null;
    return JSON.parse(raw) as ProductCheckoutNavigationState;
  } catch {
    return null;
  }
}

export function consumeProductCheckoutState(
  productId: string
): ProductCheckoutNavigationState | null {
  const state = readProductCheckoutState(productId);
  if (!state || typeof window === 'undefined') return state;
  try {
    sessionStorage.removeItem(getProductCheckoutSessionKey(productId));
  } catch {
    // Checkout can still use the parsed state.
  }
  return state;
}

export function openProductCheckoutInNewTab(
  productId: string,
  state?: ProductCheckoutNavigationState
) {
  if (state && Object.keys(state).length > 0) {
    stashProductCheckoutState(productId, state);
  }
  window.open(getProductPublicUrl(productId), '_blank', 'noopener,noreferrer');
}

export function formatProductRevenue(amount?: number | string | null) {
  const value = Number(amount) || 0;
  if (value === 0) return '$0';
  const hasCents = Math.round(value * 100) % 100 !== 0;
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function getProductTypeLabel(productType: string) {
  if (productType === 'freebie') return 'Freebie';
  if (productType === 'digital_product') return 'Digital Product';
  return productType;
}

export function productRecordToPublishContext(product: any) {
  const meta = product?.metadata || {};
  const details = meta.product_details || {};
  const delivery = meta.delivery || {};
  const productType: StoreProductType =
    product?.name === 'digital_product' ? 'digital_product' : 'freebie';
  const deliveryType: ProductDeliveryType =
    delivery.type === 'redirect' ? 'redirect' : 'file';
  const files = Array.isArray(delivery.content) ? delivery.content : [];
  const redirectUrl = deliveryType === 'redirect' ? String(delivery.content || '') : '';

  const customFields: CustomField[] = (meta.collect_info?.required_fields || [])
    .filter((field: { label?: string }) => field.label && !['Name', 'Email'].includes(field.label))
    .map((field: { type?: string; label: string; required?: boolean; icon?: string }) => ({
      type: field.type || 'text',
      label: field.label,
      required: Boolean(field.required),
      icon: field.icon,
    }));

  const emailTemplate = meta.email_template ||
    product?.email_format || { subject: '', body: '' };

  const formData: ProductFormData = {
    title: details.title || '',
    subtitle: details.subtitle || '',
    buttonText: details.buttonText || '',
    displayType: details.displayType === 'full_view' ? 'full_view' : 'link',
    thumbnailUrl: details.thumbnailUrl || product?.product_thumbnail || '',
    coverImageUrl: details.coverImageUrl || product?.product_image || '',
    description: details.description || product?.description || '',
    bottomTitle: details.bottomTitle || '',
    checkoutCustomMessage: details.checkoutCustomMessage || '',
    price: Number(product?.price) || 0,
    files,
    emailTemplate,
  };

  return {
    productType,
    formData,
    deliveryType,
    redirectUrl,
    uploadedFilesCount: files.length,
    customFields,
  };
}

export function getProductPublishValidationIssues(product: any) {
  const context = productRecordToPublishContext(product);
  return getPublishValidationIssues(
    context.productType,
    context.formData,
    context.deliveryType,
    context.redirectUrl,
    context.uploadedFilesCount
  );
}
